import { SupportedCodeLang } from "../domain/code-gen/code-lang.types";
import { getCodeLangConfig, isSupportedCodeLang } from "../domain/code-gen/code-lang.utils";

const DEFAULT_MODULE = "common";
type ModuleEntry = string | { [moduleName: string]: string[] };

const CODE_LANG_MODULES = new Map<SupportedCodeLang, ModuleEntry[]>([
  ['python', [
    DEFAULT_MODULE,
    "iot",
    "aiot",
    { aiot_v2: ["aiot_v2", "iot"] },
    "robotics",
    "data_science",
    "artificial_intelligence",
    "iiot",
    "admin_tools",
  ]],
  ['arduino', [DEFAULT_MODULE, "iot", "robotics"]]
]);

const cleanToolboxXML = (xml: string): string =>
  xml.replace(/<!--[\s\S]*?-->/g, "") || "";

const fetchXml = async (toolboxPath: any, fileName: string): Promise<string> => {
  try {
    const url = new URL(`${toolboxPath}${fileName}.xml`, document.baseURI).toString();
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return cleanToolboxXML(await res.text());
  } catch {
    return "";
  }
};

const loadModules = async (
  codeLang: SupportedCodeLang,
  modules: ModuleEntry[]
): Promise<Map<string, string>> => {
  const { toolboxPath } = getCodeLangConfig(codeLang);

  return new Map(
    await Promise.all(
      modules.map(async entry => {
        const [moduleName, files] =
          typeof entry === "string"
            ? [entry, [entry]]
            : Object.entries(entry)[0];

        const xmlFragments = await Promise.all(
          files.map(fileName => fetchXml(toolboxPath, fileName))
        );

        return [moduleName.toLowerCase(), xmlFragments.join("")] as const;
      })
    )
  );
};

export const toolboxRegistry: Promise<Map<string, Map<string, string>>> = (async () => {
  const registry = new Map<string, Map<string, string>>();
  await Promise.all(
    Array.from(CODE_LANG_MODULES.entries()).map(async ([codeLang, modules]) => {
      const moduleMap = await loadModules(codeLang, modules);
      if (moduleMap.has(DEFAULT_MODULE)) {
        registry.set(codeLang.toLowerCase(), moduleMap);
      }
    })
  );
  return registry;
})();

const wrapXml = (content: string): string => `
<xml xmlns="https://developers.google.com/blockly/xml" id="toolbox" style="display:none">
${content}
</xml>`;

export const getToolbox = async (
  codeLang: SupportedCodeLang,
  moduleName?: string
): Promise<string> => {
  if (!codeLang || !isSupportedCodeLang(codeLang)) return "";

  const registry = await toolboxRegistry;
  const moduleSet = registry.get(codeLang.toLowerCase());
  if (!moduleSet) return "";

  const defaultModuleXml = moduleSet.get(DEFAULT_MODULE) || "";

  if (moduleName?.trim()) {
    const moduleXml = moduleSet.get(moduleName.toLowerCase());
    if (moduleXml) return wrapXml(defaultModuleXml + moduleXml);
  }

  const combinedXml = [...moduleSet.entries()]
    .filter(([key]) => key !== DEFAULT_MODULE)
    .map(([, xml]) => xml)
    .join("");

  return wrapXml(defaultModuleXml + combinedXml);
};